import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import styles from "./EntityEditor.styles";
import GeneralFieldsBlock from "./entity-editor-blocks/GeneralFieldsBlock";
import EntityAddressBlock, { EntityAddressBlockHandle } from "./entity-editor-blocks/EntityAddressBlock";
import EntityPhoneNumbersBlock, { EntityPhoneNumbersBlockHandle } from './entity-editor-blocks/EntityPhoneNumbersBlock';
import EntityEmailsBlock, { EntityEmailsBlockHandle } from './entity-editor-blocks/EntityEmailsBlock';
import EntitySocialBlock, { SocialBlockHandle } from "./entity-editor-blocks/EntitySocialBlock";
import EntityGDriveBlock from "./entity-editor-blocks/EntityGDriveBlock";
import { createNewEntityRequest } from "../../../modules/add-new-entity/actions";
import AlertService from "../../services/notification.service";
import { companyNameValidator, entityFormValidator, kadIdValidator } from '../helpers/validator';
import { entityTypes } from "../../constants/field-constants";
import { selectLeadgen } from "../../../modules/leadgen/selectors";
import { updateEntityRequest } from "../../../modules/leadgen/actions/entityDetails";
import type { TouchedErrorsFieldsProps } from '../../../../types/entity-editor.types';
import type { RootState } from '../../../reducers';

// interface EntityEditorProps {
//   type: string;
//   typeEdit?: string;
//   closeModal?: any;
//   entityId?: string;
// }

const initialErrorsState = {
  isFilledLastName: true,
  isFilledCompanyName: true,
  // isFilledKadId: true,
};

const initialTouchedFieldsState: TouchedErrorsFieldsProps = {
  first_name: false,
  prefix: false,
  last_name: false,
  initials: false,
  all_names: false,
  nick_name: false,
  birth_place: false,
  birth_year: false,
  kadaster_personal_number: false,
  kadaster_objects_amount: false,
  url: false,
  kadaster_url: false,
  company_name: false,
  chamber_commerce_number: false,
  chamber_commerce_url: false,

}

const link =
  "https://kadaster-on-line.kadaster.nl/selectie.asp?identificatie=NL.IMKAD.Persoon.";
const kvkLink = "https://www.kvk.nl/orderstraat/product-kiezen/?kvknummer=";

const EntityEditor = (props: any) => {
  const initialEntityState = {
    general_fields: {
      entity_type: props.type,
      first_name: "",
      prefix: "",
      last_name: "",
      initials: "",
      all_names: "",
      nick_name: "",
      gender: "",
      blacklisted: "",
      is_resident: "",
      birth_date: "",
      birth_year: "",
      birth_place: "",
      is_dead: "",
      kadaster_personal_number: "",
      kadaster_objects_amount: "",
      kadaster_url: "",
      url: "",
      company_name: "",
      chamber_commerce_url: "",
      chamber_commerce_number: "",
    },
    addresses: [],
    phone_numbers: [],
    emails: [],
    socials: [],
    company_id: "",
    entity_company: {},
    addressesForDelete: [],
    phoneNumbersForDelete: [],
    emailsForDelete: [],
    socialsForDelete: [],
  };
  type EntityForSave = typeof initialEntityState;
  type GeneralFields = EntityForSave["general_fields"];
  type GeneralFieldKey = keyof GeneralFields;
  const [entityForSave, setEntityForSave] = useState<EntityForSave>(initialEntityState);
  const [actionTypes, setActionTypes] = useState<Array<string>>([]);
  const [errors, setErrors] = useState(initialErrorsState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { entityMainDetails, typeEdit, entityId } = props;
  const minDate = "1900-01-01";
  const today = new Date().toISOString().split("T")[0];
  const [errorDate, setErrorDate] = useState(false);
  // const [touched, setTouched] = useState(initialTouchedFieldsState);
  const [errorFields, setErrorFields] = useState<TouchedErrorsFieldsProps>(initialTouchedFieldsState);
  const [inputValue, setInputValue] = useState(entityForSave.general_fields.birth_year || "");
  const [inputValueCity, setInputValueCity] = useState( entityForSave.general_fields.birth_place || "");
  const lastErrorRef = useRef<string | null>(null);
  const socialRef = useRef<SocialBlockHandle | null>(null);
  const addressRef = useRef<EntityAddressBlockHandle | null>(null);
  const phoneNumberRef = useRef<EntityPhoneNumbersBlockHandle | null>(null);
  const emailsRef = useRef<EntityEmailsBlockHandle | null>(null);
  useEffect(() => {
    if (typeEdit === "entityEdit") {
      setEntityForSave({
        ...entityForSave,
        general_fields: {
          ...entityForSave.general_fields,
          entity_type: entityMainDetails.entity_type,
          first_name: entityMainDetails.first_name || "",
          prefix: entityMainDetails.prefix || "",
          last_name: entityMainDetails.last_name || "",
          initials: entityMainDetails.initials || "",
          all_names: entityMainDetails.all_names || "",
          nick_name: entityMainDetails.nick_name || "",
          gender: entityMainDetails.gender || "",
          blacklisted: entityMainDetails?.blacklisted ?? "",
          is_resident: entityMainDetails?.is_resident ?? "",
          birth_date: entityMainDetails.birth_date ?
            new Date(entityMainDetails.birth_date).toISOString().slice(0, 10) :
            "",
          birth_year: entityMainDetails.birth_year || "",
          birth_place: entityMainDetails.birth_place || "",
          is_dead: entityMainDetails?.is_dead ?? "",
          kadaster_personal_number:
            entityMainDetails.kadaster_personal_number || "",
          kadaster_objects_amount:
            entityMainDetails.kadaster_objects_amount || "",
          kadaster_url: entityMainDetails.kadaster_url || "",
          url: entityMainDetails.url || "",
          company_name: entityMainDetails.company_name || "",
          chamber_commerce_url: entityMainDetails.chamber_commerce_url || "",
          chamber_commerce_number:
            entityMainDetails.chamber_commerce_number || "",
        },
        addresses: entityMainDetails.addresses || [],
        phone_numbers: entityMainDetails.phone_numbers || [],
        emails: entityMainDetails.emails || [],
        socials: entityMainDetails.socials || [],
        company_id: entityMainDetails.company_id || "",
        entity_company: entityMainDetails.entity_company || {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityMainDetails, typeEdit]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      let currentError: string | null  = null;

      if (entityForSave.general_fields.birth_date && (entityForSave.general_fields.birth_date < minDate || entityForSave.general_fields.birth_date > today)) {
        currentError = 'invalidDateRange';
      } else if (entityForSave.general_fields.birth_year && entityForSave.general_fields.birth_date) {
        const dateYear = new Date(entityForSave.general_fields.birth_date).getFullYear();
        if (parseInt(entityForSave.general_fields.birth_year) !== dateYear) {
          currentError = 'yearMismatch';
        }
      }

      if (currentError && currentError !== lastErrorRef.current) {
        if (currentError === 'yearMismatch') {
          AlertService.warning('Please make sure that the year in the "Y.O.B" field matches the year in the "D.O.B".');
          setErrorDate(true);
          setErrorFields?.(prev => ({ ...prev, birth_year: true }));
        }
        if (currentError === 'invalidDateRange') {
          AlertService.warning('Invalid date range');
          setErrorDate(true);
        }

        lastErrorRef.current = currentError;
      }
      if (!currentError) {
        lastErrorRef.current = null;
        setErrorDate(false);
        setErrorFields?.(prev => ({...prev, birth_year: false}));
      }
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityForSave.general_fields.birth_year, entityForSave.general_fields.birth_date]);

  const handleChange = <K extends GeneralFieldKey>(
    name: string,
    key: K,
    value: GeneralFields[K],
  ) => {
    // let input: any;s
    // console.log("EVENT CHANGE", typeof event);
    const input = value;
    // if (key === "birth_year") {
    //    input = event;
    // } else {
    //   if (key === "birth_date") {
    //     input = event;
    //   } else {
    //     input = event?.target?.value;
    //   }
    // }

    if (name === "generalFields") {
      if (!actionTypes.includes("general_fields")) {
        setActionTypes([...actionTypes, "general_fields"]);
      }

      if (key === "kadaster_personal_number") {
        return setEntityForSave({
          ...entityForSave,
          general_fields: {
            ...entityForSave.general_fields,
            [key]: input,
            kadaster_url: link + input,
          },
        });
      }
      if (key === "chamber_commerce_number") {
        return setEntityForSave({
          ...entityForSave,
          general_fields: {
            ...entityForSave.general_fields,
            [key]: input,
            chamber_commerce_url: input
              ? kvkLink + input
              : "",
          },
        });
      }

      if (
        entityForSave.general_fields.entity_type === entityTypes.PERSON &&
        key === "last_name"
      ) {
        setErrors({...errors, isFilledCompanyName: true, isFilledLastName: true });
      }
      if (
        entityForSave.general_fields.entity_type === entityTypes.COMPANY &&
        key === "company_name"
      ) {
        setErrors({...errors, isFilledLastName: true, isFilledCompanyName: true });
      }
      setEntityForSave({
        ...entityForSave,
        general_fields: {
          ...entityForSave.general_fields,
          [key]: input,
        },
      });
    }
  };

  const updateEntity = (
    name: string,
    item: any,
    editMode: boolean,
  ) => {
    if (!actionTypes.includes(name)) {
      setActionTypes([...actionTypes, name]);
    }
    if (name === "entity_addresses") {
      if (editMode) {
        return setEntityForSave({
          ...entityForSave,
          // @ts-ignore
          addresses: [...item.filteredList],
          // @ts-ignore
          addressesForDelete: [...item.newStateForDelete],
        });
      }
      return setEntityForSave({
        ...entityForSave,
        // @ts-ignore
        addresses: [...item],
      });
    }
    if (name === "entity_phone_numbers") {
      if (editMode) {
        return setEntityForSave({
          ...entityForSave,
          // @ts-ignore
          phone_numbers: [...item.filteredList],
          // @ts-ignore
          phoneNumbersForDelete: [
            ...entityForSave.phoneNumbersForDelete,
            ...item.newStateForDelete,
          ],
        });
      }
      return setEntityForSave({
        ...entityForSave,
        // @ts-ignore
        phone_numbers: [...item],
      });
    }
    if (name === "entity_emails") {
      if (editMode) {
        return setEntityForSave({
          ...entityForSave,
          // @ts-ignore
          emails: [...item.filteredList],
          // @ts-ignore
          emailsForDelete: [
            ...entityForSave.emailsForDelete,
            ...item.newStateForDelete,
          ],
        });
      }
      return setEntityForSave({
        ...entityForSave,
        // @ts-ignore
        emails: [...item],
      });
    }
    if (name === "entity_socials") {
      if (editMode) {
        return setEntityForSave({
          ...entityForSave,
          // @ts-ignore
          socials: [...item.filteredList],
          // @ts-ignore
          socialsForDelete: [
            ...entityForSave.socialsForDelete,
            ...item.newStateForDelete,
          ],
        });
      }
      return setEntityForSave({
        ...entityForSave,
        // @ts-ignore
        socials: [...item],
      });
    }
    if (name === "entity_company_id") {
      return setEntityForSave({
        ...entityForSave,
        company_id: item.length > 0 && item[0]?.id ? item[0]?.id : null,
      });
    }
  };

  const handleSave = async () => {
    let isExistedKadId: any;
    if (entityForSave.general_fields.kadaster_personal_number) {
      const type = "entity";
      isExistedKadId = await kadIdValidator(
        type,
        entityForSave.general_fields.kadaster_personal_number
      );
      if (isExistedKadId?.data && isExistedKadId?.data?.id !== entityId) {
        setErrorFields({ ...errorFields, kadaster_personal_number: true });
        return AlertService.warning("Entity with this KAD ID already exists");
      } else { setErrorFields({ ...errorFields, kadaster_personal_number: false }); }
    }

    if (entityForSave.general_fields.entity_type === entityTypes.COMPANY) {
      let isExistedCompanyName: any;
      if (entityForSave.general_fields.company_name) {
        const type = "entity";
        isExistedCompanyName = await companyNameValidator(
          type,
          entityForSave.general_fields.company_name
        )
        if (isExistedCompanyName?.data && isExistedCompanyName?.data?.id !== entityId) {
          setErrorFields({ ...errorFields, company_name: true });
          return AlertService.warning("Entity with this Company Name already exists");
        } else { setErrorFields({ ...errorFields, company_name: false }); }
      }
    }

    const isValidEntity = entityFormValidator(entityForSave);
    setErrors({...errors, ...isValidEntity});
    if (
      entityForSave.general_fields.entity_type === entityTypes.PERSON &&
      isValidEntity.isFilledLastName && !errorDate && Object.values(errorFields).every((e) => !e)
    ) {
      if (typeEdit === "entityEdit") {
        dispatch(
          updateEntityRequest(true, actionTypes, entityId, entityForSave)
        );
        props.closeModal();
      }
      if (typeEdit !== "entityEdit") {
        dispatch(createNewEntityRequest(entityForSave, navigate));
      }
    } else if (
      entityForSave.general_fields.entity_type === entityTypes.COMPANY &&
      isValidEntity.isFilledCompanyName && Object.values(errorFields).every((e) => !e)
    ) {
      if (typeEdit === "entityEdit") {
        dispatch(
          updateEntityRequest(true, actionTypes, entityId, entityForSave)
        );
        props.closeModal();
      }
      if (typeEdit !== "entityEdit") {
        dispatch(createNewEntityRequest(entityForSave, navigate));
      }
    } else {
      return AlertService.warning("fields are not valid");
    }
  };

  const handleCancel = () => {
    setErrors(initialErrorsState);
    // setTouched(initialTouchedFieldsState);
    setErrorFields(initialTouchedFieldsState);
    setInputValue('');
    setErrorDate(false);
    setEntityForSave(initialEntityState);
    socialRef.current?.reset();
    addressRef.current?.reset();
    phoneNumberRef.current?.reset();
    emailsRef.current?.reset();

  };

  return (
    <Box sx={styles.root}>
      <GeneralFieldsBlock
        entityType={props.type}
        onChange={handleChange}
        entityFields={entityForSave.general_fields}
        entityCompany={entityForSave.entity_company}
        onChangeCompany={updateEntity}
        errors={errors}
        min={minDate}
        errorDate={errorDate}
        errorFields={errorFields}
        setErrorFields={setErrorFields}
        inputValue={inputValue}
        setInputValue={setInputValue}
        inputValueCity={inputValueCity}
        setInputValueCity={setInputValueCity}
      />
      <EntityAddressBlock
        ref={addressRef}
        entityAddresses={entityForSave.addresses}
        onChange={updateEntity}
        typeEdit={typeEdit}
      />
      <EntityPhoneNumbersBlock
        ref={phoneNumberRef}
        phoneNumbers={entityForSave.phone_numbers}
        onChange={updateEntity}
      />
      <EntityEmailsBlock
        ref={emailsRef}
        emails={entityForSave.emails}
        onChange={updateEntity}
      />
      <EntitySocialBlock
        ref={socialRef}
        socials={entityForSave.socials}
        onChange={updateEntity}
      />
      <EntityGDriveBlock driveLink={""} onChange={updateEntity} />
      <Box sx={styles.buttonHolder}>
        <Button variant="contained" sx={styles.buttonSave} onClick={handleSave}>
          Save
        </Button>
        <Button
          variant="contained"
          type="button"
          sx={styles.buttonCansel}
          onMouseDown={(e) => e.preventDefault()}
          onClick={typeEdit === "entityEdit" ? props.closeModal : handleCancel}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

function mapStateToProps(state: RootState) {
  const { entityMainDetails } = selectLeadgen(state);
  return { entityMainDetails };
}

export default connect(mapStateToProps)(EntityEditor);
